import { API_ROUTES } from "@/common/constants/routes"
import { BaseDRSProvider } from "@/governance/helpers/drs/providers/BaseDRSProvider"
import {
  type CuriaApiResponse,
  type DRSConfig,
  type DRSInfo,
  DRSProviderType,
  DRSStatus,
} from "@/governance/types/drs"

export class CuriaDRSProvider extends BaseDRSProvider {
  private drsName: string

  constructor(config: DRSConfig) {
    super(config)
    this.drsName = config.name
  }

  protected async fetchSingleAddress(address: string): Promise<DRSInfo | null> {
    const response = await fetch(
      API_ROUTES.curia.getByAddress(this.drsName, address),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch DRS info: ${response.status}`)
    }

    const { data, curiaUpdatedAt } = await response.json()

    if (!data) {
      return this.createEmptyDRSInfo(address)
    }

    return this.transformToDRSInfo(data, curiaUpdatedAt)
  }

  protected async fetchBulkAddresses(
    addresses: string[],
  ): Promise<Map<string, DRSInfo>> {
    const map = new Map<string, DRSInfo>()

    const response = await fetch(
      API_ROUTES.curia.getByAddresses(this.drsName),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ addresses }),
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch bulk DRS info: ${response.status}`)
    }

    const { data, drsUpdatedAt } = await response.json()

    addresses.forEach((address) => {
      const normalizedAddress = address.toLowerCase()
      const curiaData = data[normalizedAddress]

      if (curiaData) {
        map.set(
          normalizedAddress,
          this.transformToDRSInfo(curiaData, drsUpdatedAt),
        )
      } else {
        map.set(normalizedAddress, this.createEmptyDRSInfo(address))
      }
    })

    return map
  }

  protected getProbationSupport(): boolean {
    return true // Curia supports probation
  }

  private transformToDRSInfo(
    curiaData: CuriaApiResponse,
    curiaUpdatedAt?: string | null,
  ): DRSInfo {
    const status = this.determineStatus(curiaData.status)

    return {
      address: curiaData.address.toLowerCase(),
      status,
      drsScore: Math.floor(curiaData.delegate_score),
      probationSupported: true,
      isProbation: curiaData?.is_probation,
      lastUpdated: curiaData.latest_updated_at || curiaUpdatedAt || null,
      externalLink: this.buildExternalLink(curiaData.address),
      provider: DRSProviderType.CURIA,
      isEligible: status === DRSStatus.ACTIVE,
      username: curiaData.username ?? null,
      hasInfo: true,
    }
  }

  private determineStatus(curiaStatus: string): DRSStatus {
    const normalizedStatus = curiaStatus.toUpperCase()

    if (normalizedStatus === "ACTIVE") return DRSStatus.ACTIVE
    if (normalizedStatus === "INACTIVE") return DRSStatus.INACTIVE
    if (normalizedStatus === "GHOST") return DRSStatus.GHOST

    // Default to INACTIVE for unknown statuses
    return DRSStatus.INACTIVE
  }
}
